import React, { useState, useEffect, useRef, useCallback } from "react";

// 分子类
class Molecule {
  constructor(x, y, vx, vy, radius = 5) {
    this.x = x;
    this.y = y;
    this.vx = vx;
    this.vy = vy;
    this.radius = radius;
    this.mass = 1;
  }

  // 更新位置
  update() {
    this.x += this.vx;
    this.y += this.vy;
  }

  // 获取速度大小
  getSpeed() {
    return Math.sqrt(this.vx * this.vx + this.vy * this.vy);
  }

  // 获取动能
  getKineticEnergy() {
    return 0.5 * this.mass * (this.vx * this.vx + this.vy * this.vy);
  }
}

// 颜色映射函数：根据速度返回颜色（优化对比度，适合黑色背景）
const getSpeedColor = (speed, maxSpeed) => {
  const ratio = Math.min(speed / maxSpeed, 1);

  if (ratio < 0.2) {
    // 深蓝色 (极低速)
    return `rgb(30, 60, 200)`;
  } else if (ratio < 0.4) {
    // 深蓝到亮蓝 (低速)
    const intensity = (ratio - 0.2) * 5;
    const r = Math.floor(30 + intensity * 70);
    const g = Math.floor(60 + intensity * 140);
    const b = 255;
    return `rgb(${r}, ${g}, ${b})`;
  } else if (ratio < 0.6) {
    // 亮蓝到青绿 (中低速)
    const intensity = (ratio - 0.4) * 5;
    const r = Math.floor(100 - intensity * 100);
    const g = 255;
    const b = Math.floor(255 - intensity * 55);
    return `rgb(${r}, ${g}, ${b})`;
  } else if (ratio < 0.8) {
    // 青绿到橙黄 (中高速)
    const intensity = (ratio - 0.6) * 5;
    const r = Math.floor(intensity * 255);
    const g = 255;
    const b = Math.floor(200 - intensity * 200);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // 橙黄到深红 (高速)
    const intensity = (ratio - 0.8) * 5;
    const r = 255;
    const g = Math.floor(255 - intensity * 155);
    const b = 0;
    return `rgb(${r}, ${g}, ${b})`;
  }
};

const MolecularTheorySimulator = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const moleculesRef = useRef([]);
  const pixelRatioRef = useRef(1);

  const [isRunning, setIsRunning] = useState(true);
  const [temperature, setTemperature] = useState(300); // 开尔文
  const [showArrows, setShowArrows] = useState(false);
  const [moleculeCount, setMoleculeCount] = useState(60);
  const [maxSpeed, setMaxSpeed] = useState(5);

  const canvasWidth = 600;
  const canvasHeight = 400;

  // 初始化分子
  const initializeMolecules = useCallback(() => {
    const molecules = [];
    const tempFactor = Math.sqrt(temperature / 300); // 温度因子

    for (let i = 0; i < moleculeCount; i++) {
      let x, y;
      let attempts = 0;

      // 避免分子重叠
      do {
        x = Math.random() * (canvasWidth - 20) + 10;
        y = Math.random() * (canvasHeight - 20) + 10;
        attempts++;
      } while (
        attempts < 50 &&
        molecules.some(
          (mol) => Math.sqrt((mol.x - x) ** 2 + (mol.y - y) ** 2) < 20
        )
      );

      // 根据麦克斯韦-玻尔兹曼分布生成速度
      const angle = Math.random() * 2 * Math.PI;
      const speed = (Math.random() * 2 + 0.5) * tempFactor;

      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;

      molecules.push(new Molecule(x, y, vx, vy));
    }

    moleculesRef.current = molecules;
  }, [moleculeCount, temperature]);

  // 调整分子速度以匹配温度
  const adjustMoleculeSpeeds = useCallback(() => {
    const tempFactor = Math.sqrt(temperature / 300);

    moleculesRef.current.forEach((molecule) => {
      const currentSpeed = molecule.getSpeed();
      if (currentSpeed > 0) {
        const newSpeed = (Math.random() * 2 + 0.5) * tempFactor;
        const speedRatio = newSpeed / currentSpeed;
        molecule.vx *= speedRatio;
        molecule.vy *= speedRatio;
      }
    });
  }, [temperature]);

  // 碰撞检测和处理
  const handleCollisions = () => {
    const molecules = moleculesRef.current;

    // 分子与墙壁碰撞
    molecules.forEach((molecule) => {
      if (
        molecule.x <= molecule.radius ||
        molecule.x >= canvasWidth - molecule.radius
      ) {
        molecule.vx = -molecule.vx;
        molecule.x = Math.max(
          molecule.radius,
          Math.min(canvasWidth - molecule.radius, molecule.x)
        );
      }
      if (
        molecule.y <= molecule.radius ||
        molecule.y >= canvasHeight - molecule.radius
      ) {
        molecule.vy = -molecule.vy;
        molecule.y = Math.max(
          molecule.radius,
          Math.min(canvasHeight - molecule.radius, molecule.y)
        );
      }
    });

    // 分子间碰撞
    for (let i = 0; i < molecules.length; i++) {
      for (let j = i + 1; j < molecules.length; j++) {
        const mol1 = molecules[i];
        const mol2 = molecules[j];

        const dx = mol2.x - mol1.x;
        const dy = mol2.y - mol1.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < mol1.radius + mol2.radius) {
          // 计算碰撞向量
          const nx = dx / distance;
          const ny = dy / distance;

          // 相对速度
          const dvx = mol2.vx - mol1.vx;
          const dvy = mol2.vy - mol1.vy;

          // 相对速度在碰撞法线方向的分量
          const dvn = dvx * nx + dvy * ny;

          // 如果分子正在分离，不处理碰撞
          if (dvn > 0) continue;

          // 弹性碰撞：交换法线方向的速度分量
          const impulse = (2 * dvn) / (mol1.mass + mol2.mass);

          mol1.vx += impulse * mol2.mass * nx;
          mol1.vy += impulse * mol2.mass * ny;
          mol2.vx -= impulse * mol1.mass * nx;
          mol2.vy -= impulse * mol1.mass * ny;

          // 分离重叠的分子
          const overlap = mol1.radius + mol2.radius - distance;
          const separationX = nx * overlap * 0.5;
          const separationY = ny * overlap * 0.5;

          mol1.x -= separationX;
          mol1.y -= separationY;
          mol2.x += separationX;
          mol2.y += separationY;
        }
      }
    }
  };

  // 设置高DPI canvas
  const setupHighDPICanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const pixelRatio = window.devicePixelRatio || 1;
    pixelRatioRef.current = pixelRatio;

    // 设置canvas的实际尺寸
    canvas.width = canvasWidth * pixelRatio;
    canvas.height = canvasHeight * pixelRatio;

    // 设置canvas的显示尺寸
    canvas.style.width = canvasWidth + "px";
    canvas.style.height = canvasHeight + "px";

    // 缩放绘制上下文
    ctx.scale(pixelRatio, pixelRatio);

    // 设置文字渲染优化
    ctx.textBaseline = "top";
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
  }, []);

  // 绘制函数
  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const pixelRatio = pixelRatioRef.current;

    // 清除画布
    ctx.save();
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.restore();

    // 黑色背景
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // 绘制容器边界（亮灰色）
    ctx.strokeStyle = "#888888";
    ctx.lineWidth = 3;
    ctx.strokeRect(1.5, 1.5, canvasWidth - 3, canvasHeight - 3);

    const molecules = moleculesRef.current;
    const currentMaxSpeed = Math.max(...molecules.map((m) => m.getSpeed()), 1);

    molecules.forEach((molecule) => {
      const speed = molecule.getSpeed();
      const color = getSpeedColor(speed, currentMaxSpeed);

      // 绘制分子
      ctx.beginPath();
      ctx.arc(molecule.x, molecule.y, molecule.radius, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();

      // 分子边缘（深色轮廓）
      ctx.strokeStyle = "#333333";
      ctx.lineWidth = 1;
      ctx.stroke();

      // 绘制速度箭头
      if (showArrows && speed > 0.1) {
        const arrowLength = Math.min(speed * 12, 35);
        const headLength = 8;

        // 计算箭头尖端位置
        const endX = molecule.x + (molecule.vx / speed) * arrowLength;
        const endY = molecule.y + (molecule.vy / speed) * arrowLength;

        // 计算箭头主体结束位置（箭头头部基部）
        const bodyEndX =
          molecule.x + (molecule.vx / speed) * (arrowLength - headLength);
        const bodyEndY =
          molecule.y + (molecule.vy / speed) * (arrowLength - headLength);

        // 绘制箭头主体
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(molecule.x, molecule.y);
        ctx.lineTo(bodyEndX, bodyEndY);
        ctx.stroke();

        // 绘制箭头头部（填充三角形）
        const angle = Math.atan2(molecule.vy, molecule.vx);
        const headWidth = 5;

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(
          endX - headLength * Math.cos(angle - Math.PI / 6),
          endY - headLength * Math.sin(angle - Math.PI / 6)
        );
        ctx.lineTo(
          endX - headLength * Math.cos(angle + Math.PI / 6),
          endY - headLength * Math.sin(angle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fill();
      }
    });

    // 显示信息（白色文字，更大更清晰）
    ctx.fillStyle = "#ffffff";
    ctx.font =
      'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.textBaseline = "top";

    ctx.fillText(`温度: ${temperature} K`, 20, 20);
    ctx.fillText(`分子数量: ${molecules.length}`, 20, 50);

    const avgSpeed =
      molecules.reduce((sum, mol) => sum + mol.getSpeed(), 0) /
      molecules.length;
    ctx.fillText(`平均速度: ${avgSpeed.toFixed(2)}`, 20, 80);
  };

  // 动画循环
  const animate = () => {
    if (!isRunning) return;

    handleCollisions();
    moleculesRef.current.forEach((molecule) => molecule.update());
    draw();

    animationRef.current = requestAnimationFrame(animate);
  };

  // 初始化和清理
  useEffect(() => {
    setupHighDPICanvas();
    initializeMolecules();
  }, [setupHighDPICanvas, initializeMolecules, moleculeCount]);

  useEffect(() => {
    if (isRunning) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isRunning]);

  useEffect(() => {
    adjustMoleculeSpeeds();
  }, [temperature, adjustMoleculeSpeeds]);

  useEffect(() => {
    setupHighDPICanvas();
    draw();
  }, [showArrows, setupHighDPICanvas]);

  return (
    <div className="flex flex-col items-center p-6 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">
        分子动理论模拟器
      </h1>

      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <canvas
          ref={canvasRef}
          className="border-2 border-gray-400 rounded bg-black"
          style={{
            width: `${canvasWidth}px`,
            height: `${canvasHeight}px`,
            imageRendering: "crisp-edges",
          }}
        />
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                温度: {temperature} K ({(temperature - 273.15).toFixed(1)} °C)
              </label>
              <input
                type="range"
                min="100"
                max="800"
                value={temperature}
                onChange={(e) => setTemperature(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                分子数量: {moleculeCount}
              </label>
              <input
                type="range"
                min="15"
                max="120"
                value={moleculeCount}
                onChange={(e) => setMoleculeCount(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsRunning(!isRunning)}
                className={`px-4 py-2 rounded-lg font-medium ${
                  isRunning
                    ? "bg-red-500 hover:bg-red-600 text-white"
                    : "bg-green-500 hover:bg-green-600 text-white"
                }`}
              >
                {isRunning ? "暂停" : "开始"}
              </button>

              <button
                onClick={initializeMolecules}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium"
              >
                重置
              </button>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="showArrows"
                checked={showArrows}
                onChange={(e) => setShowArrows(e.target.checked)}
                className="mr-2"
              />
              <label
                htmlFor="showArrows"
                className="text-sm font-medium text-gray-700"
              >
                显示速度箭头
              </label>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-2">颜色说明：</h3>
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center">
              <div
                className="w-4 h-4 rounded mr-2"
                style={{ backgroundColor: "rgb(30, 60, 200)" }}
              ></div>
              <span>极低速</span>
            </div>
            <div className="flex items-center">
              <div
                className="w-4 h-4 rounded mr-2"
                style={{ backgroundColor: "rgb(100, 200, 255)" }}
              ></div>
              <span>低速</span>
            </div>
            <div className="flex items-center">
              <div
                className="w-4 h-4 rounded mr-2"
                style={{ backgroundColor: "rgb(0, 255, 200)" }}
              ></div>
              <span>中速</span>
            </div>
            <div className="flex items-center">
              <div
                className="w-4 h-4 rounded mr-2"
                style={{ backgroundColor: "rgb(255, 255, 0)" }}
              ></div>
              <span>高速</span>
            </div>
            <div className="flex items-center">
              <div
                className="w-4 h-4 rounded mr-2"
                style={{ backgroundColor: "rgb(255, 100, 0)" }}
              ></div>
              <span>极高速</span>
            </div>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          <p>
            <strong>操作说明：</strong>
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>调节温度滑块可以观察分子运动的变化</li>
            <li>勾选"显示速度箭头"可以看到分子的运动方向和速度</li>
            <li>分子颜色表示其运动速度，从蓝色(慢)到红色(快)</li>
            <li>温度越高，分子平均运动速度越快</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MolecularTheorySimulator;
